import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import LoadingSpinner from '../components/LoadingSpinner';
import { supabase } from '../config/supabase';
import toast from 'react-hot-toast';

/**
 * Page d'affichage des factures Stripe
 */
const InvoicesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Rediriger si non authentifié
    if (!user) {
      navigate('/settings');
      return;
    }

    fetchInvoices();
  }, [user, navigate]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      // Récupérer le token de session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('Session non disponible');
      }

      // Appeler la fonction Edge
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/stripe-list-invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId: user.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la récupération des factures');
      }

      const data = await response.json();
      
      if (data.success) {
        setInvoices(data.invoices || []);
      } else {
        throw new Error(data.error || 'Erreur lors de la récupération des factures');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des factures:', err);
      setError(err.message);
      toast.error(t('invoices.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'EUR',
    }).format(amount / 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'text-green-600 bg-green-50';
      case 'open':
        return 'text-yellow-600 bg-yellow-50';
      case 'void':
        return 'text-gray-600 bg-gray-50';
      case 'uncollectible':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusText = (status) => {
    return t(`invoices.status.${status}`) || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title={t('invoices.title')} backPath="/settings" />
        <div className="flex justify-center items-center p-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title={t('invoices.title')} backPath="/settings" />
      
      <div className="max-w-4xl mx-auto p-4 pb-24">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800">{error}</p>
            <button
              onClick={fetchInvoices}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              {t('common.retry')}
            </button>
          </div>
        )}

        {!error && invoices.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">{t('invoices.noInvoices')}</p>
            <p className="text-gray-400 text-sm mt-2">{t('invoices.noInvoicesDescription')}</p>
          </div>
        )}

        {!error && invoices.length > 0 && (
          <div className="space-y-4">
            {invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">
                      {t('invoices.invoiceNumber', { number: invoice.number })}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {formatDate(invoice.created)}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      invoice.status
                    )}`}
                  >
                    {getStatusText(invoice.status)}
                  </span>
                </div>

                <div className="border-t border-gray-100 pt-3 mt-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">{t('invoices.amount')}</span>
                    <span className="font-semibold text-gray-900">
                      {formatAmount(invoice.amount_paid || invoice.total, invoice.currency)}
                    </span>
                  </div>

                  {invoice.description && (
                    <div className="text-sm text-gray-500 mb-2">
                      {invoice.description}
                    </div>
                  )}

                  {invoice.metadata?.credits && (
                    <div className="text-sm text-gray-600">
                      {t('invoices.credits', { count: invoice.metadata.credits })}
                    </div>
                  )}
                </div>

                {invoice.invoice_pdf && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <a
                      href={invoice.invoice_pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-bleu2 hover:text-bleu3 text-sm font-medium"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      {t('invoices.downloadPdf')}
                    </a>
                  </div>
                )}

                {invoice.hosted_invoice_url && (
                  <div className="mt-2">
                    <a
                      href={invoice.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-bleu2 hover:text-bleu3 text-sm font-medium"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                      {t('invoices.viewOnline')}
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesPage;
